/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from "react";
import type { InputProps } from "@chakra-ui/react";
import { FormControl, FormErrorMessage, FormHelperText, FormLabel, Input, InputGroup, InputRightElement, Text } from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { VscCheck, VscClose } from "react-icons/vsc";

type InputType = "cellphone" | "cpf" | "email" | "text";

interface IInputTypesDefault {
	cpf: {
		maskDefault: string;
		typeKeyboard: InputProps["inputMode"];
	};
	cellphone: {
		maskDefault: string;
		typeKeyboard: InputProps["inputMode"];
	};
	email: {
		maskDefault: string;
		typeKeyboard: InputProps["inputMode"];
	};
	text: {
		maskDefault: string;
		typeKeyboard: InputProps["inputMode"];
	};
}

interface IProps {
	label?: string;
	placeholder?: string;
	type: InputType;
	isError: boolean;
	isSuccess: boolean;
	messageError?: string;
	messageHelper?: string;
	inputProps?: InputProps;
	value: string;
	hasIcon?: boolean;
	setValue(value: string): void;
}

const maskCPF = (value: string) => value
	.replace(/\D/g, "")
	.replace(/(\d{3})(\d)/, "$1.$2")
	.replace(/(\d{3})(\d)/, "$1.$2")
	.replace(/(\d{3})(\d{1,2})$/, "$1-$2")
	.slice(0, 14);

const maskCellphone = (value: string) => value
	.replace(/\D/g, "")
	.replace(/(\d{2})(\d)/, "$1 $2")
	.replace(/(\d{1})(\d{4})(\d)/, "$1 $2-$3")
	.replace(/(\d{4})(\d{4})$/, "$1-$2")
	.slice(0, 15);

export const InputFloatingLabel: React.FC<IProps> = (props) => {
	const {
		label,
		type,
		isError,
		value,
		setValue,
		isSuccess,
		placeholder,
		messageHelper,
		messageError,
		inputProps,
		hasIcon,
	} = props;

	const [focusedInput, setFocusedInput] = useState(false);

	const setInputType = (t: InputType): IInputTypesDefault[InputType] => {
		const inputTypesDefault: IInputTypesDefault = {
			cpf: {
				maskDefault: "999.999.999-99",
				typeKeyboard: "numeric",
			},
			cellphone: {
				maskDefault: "99 9 9999-9999",
				typeKeyboard: "tel",
			},
			email : {
				maskDefault: "endereco@dominio.com.br",
				typeKeyboard: "email",
			},
			text: {
				maskDefault: "",
				typeKeyboard: "text",
			},
		};
		return inputTypesDefault[t];
	};

	const {typeKeyboard} = setInputType(type);

	const colorBorderValidation = useMemo(() => isError ? "status.error" : isSuccess ? "status.success" : "white.700", [isSuccess, isError]);
	const colorTextValidation = useMemo(() => isError ? "status.error" : isSuccess ? "status.success" : "gray.300", [isSuccess, isError]);

	const handleIcon = useCallback(() => isSuccess ?
		(<Icon as={VscCheck as any} w={5} h={5} color={"status.success"}/>) : isError ?
			(<Icon as={VscClose as any} w={5} h={5} color={"status.error"}/>) : "",[isError, isSuccess]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let val = e.target.value;
		if (type === "cpf") {
			val = maskCPF(val);
		}
		if (type === "cellphone") {
			val = maskCellphone(val);
		}
		setValue(val);
	};

	return (
		<FormControl
			variant="floating"
			id={type}
			isInvalid={isError}
			position="relative"
			maxW="636px"
		>
			<InputGroup w={["100%", "615px"]} h={16}>
				<Input
					inputMode={typeKeyboard}
					id={type}
					h={16}
					px={4}
					pt={4}
					name={type}
					maxLength={14}
					type={typeKeyboard}
					color="black.500"
					fontSize="18px"
					placeholder=" "
					maskChar={null}
					border="2px"
					lineHeight={1.5}
					borderRadius="4px"
					borderColor="white.700"
					value={value}
					borderTopColor={colorBorderValidation}
					onChange={handleChange}
					onFocus={() => setFocusedInput(true)}
					onBlur={() => setFocusedInput(false)}
					_focus={{ borderTopColor: colorBorderValidation }}
					_hover={{ borderTopColor: colorBorderValidation, borderColor: "none" }}
					errorBorderColor="none"
					{...inputProps}
				/>
				{hasIcon && (
					<InputRightElement top="50%" transform="translateY(-50%)" right={4}>
						{handleIcon()}
					</InputRightElement>
				)}

				{label ? (
					<FormLabel position={"absolute"} top={focusedInput || value.length <= 0 ? "5" : "2"} left="4">
						<Text color={colorTextValidation} fontWeight={400} fontSize={"16px"} lineHeight={1.5} >
							{value.length > 0 || focusedInput ? label : placeholder}
						</Text>
					</FormLabel>
				) : null }
			</InputGroup>

			{isError && (
				<FormErrorMessage>
					<Text
						mt={2}
						color={colorTextValidation}
						fontWeight={400}
						fontSize={{ base: "12px", lg: "14px" }}
						lineHeight={{ base: "16px", lg: "20px" }}
					>
						{messageError}
					</Text>
				</FormErrorMessage>
			)}

			{messageHelper && (
				<FormHelperText>
					<Text
						mt={2}
						color="gray.600"
						fontWeight={400}
						fontSize="14px"
						lineHeight={1.5}
					>
						{messageHelper}
					</Text>
				</FormHelperText>
			)}
		</FormControl>
	);
};

